import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { UserCircle2, FileText, Clock, DollarSign, ShieldCheck, Trash2, Send, Loader2, AlertCircle, CheckCircle2, XCircle, Scale, Ban, Video, Calendar } from "lucide-react";
import SubscriptionPlansCard from "../components/subscription/SubscriptionPlansCard";
import PostConsultationPanel from "../components/booking/PostConsultationPanel";
import ProfessionalConveniosSection from "../components/professional/ProfessionalConveniosSection";
import ProfessionalInsuranceFinancePanel from "../components/professional/ProfessionalInsuranceFinancePanel";

/**
 * Painel do profissional credenciado.
 *
 * O profissional preenche perfil, sobe documentos e configura agenda semanal.
 * Depois clica em "Enviar para aprovação" — o admin recebe e aprova/reprova.
 */

const FALLBACK_CATEGORIES = [
  // Saúde mental (core RAF Direct)
  "Psiquiatra", "Clínico Geral", "Psicólogo(a)", "Psicoterapeuta",
  "Terapeuta / Terapeuta Ocupacional", "Terapeuta Holístico",
  "Nutricionista", "Enfermeiro(a)", "Assistente Social", "Educador Físico",
  "Fisioterapeuta", "Fonoaudiólogo(a)", "Farmacêutico(a)", "Musicoterapeuta",
  "Arteterapeuta",
  // Cuidado geriátrico (core Filho Presente)
  "Geriatra", "Gerontólogo", "Cuidador de Idosos",
  "Fisioterapeuta Geriátrico", "Enfermeiro Home Care",
  "Nutricionista Geriátrico", "Fonoaudiólogo Geriátrico",
  "Terapeuta Ocupacional Geriátrico", "Médico Home Care",
  "Acompanhante Terapêutico",
  // Comum
  "Médico", "Cardiologista", "Neurologista", "Endocrinologista",
  "Ortopedista", "Pediatra", "Dentista", "Cuidador", "Outros",
];

const COUNCILS = ["CRM", "CRP", "CFN", "CRN", "CRT", "CBO", "COREN", "CREF", "CRESS", "CREFITO", "CRF", "CREFONO", "Outros"];

const WEEKDAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

const STATUS_MAP = {
  draft:    { label: "Rascunho",           cls: "bg-slate-100 text-slate-700 border-slate-300",   Icon: FileText },
  pending:  { label: "Em análise",          cls: "bg-amber-50 text-amber-800 border-amber-300",   Icon: Loader2 },
  approved: { label: "Aprovado",           cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  rejected: { label: "Reprovado",           cls: "bg-red-50 text-red-800 border-red-300",         Icon: XCircle },
};

const ProfessionalDashboard = () => {
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const photoRef = useRef(null);
  const curriculumRef = useRef(null);
  const docRef = useRef(null);

  const load = () => api.get("/professional/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Iter 68.6: fetch categories from API (fallback to hardcoded list on 404).
  useEffect(() => {
    api.get("/public/categories")
      .then((r) => {
        const names = (r.data || []).map((c) => c.name).filter(Boolean);
        if (names.length) setCategories(names);
      })
      .catch(() => {}); // silent — fallback já está setado
  }, []);

  if (!me) return <div className="p-10 text-slate-500">Carregando…</div>;

  // Gate de termos: enquanto o profissional não aceitar formalmente os termos
  // comerciais, ele NÃO vê o formulário de cadastro. Aceite fica registrado
  // no backend com timestamp e versão — evidência para acionamento das
  // cláusulas contratuais (banimento por atendimento externo, etc.).
  if (!me.terms_accepted_at) {
    return <TermsAcceptanceGate professionalName={me.name} onAccepted={load} />;
  }

  const set = (k, v) => setMe({ ...me, [k]: v });
  const toggleCat = (c) => {
    const cur = new Set(me.categories || []);
    cur.has(c) ? cur.delete(c) : cur.add(c);
    set("categories", Array.from(cur));
  };

  // Salva o rascunho atual no backend sem toast/reload. Usado antes de fazer
  // upload/delete de arquivo — evita que o load() posterior sobrescreva com
  // dados stale do backend e apague o que o usuário digitou mas ainda não salvou.
  const persistDraft = async () => {
    try {
      await api.patch("/professional/me", {
        display_name: me.display_name, phone: me.phone,
        categories: me.categories || [], council_type: me.council_type,
        council_number: me.council_number, council_state: me.council_state,
        bio: me.bio, curriculum_text: me.curriculum_text,
        languages: me.languages || [], session_duration: me.session_duration || 50,
        session_value: parseFloat(me.session_value || 0),
        availability: me.availability || [],
        timezone: me.timezone || "America/Sao_Paulo",
      });
    } catch { /* silent — o upload continua mesmo se draft falhar */ }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/professional/me", {
        display_name: me.display_name, phone: me.phone,
        categories: me.categories || [], council_type: me.council_type,
        council_number: me.council_number, council_state: me.council_state,
        bio: me.bio, curriculum_text: me.curriculum_text,
        languages: me.languages || [], session_duration: me.session_duration || 50,
        session_value: parseFloat(me.session_value || 0),
        availability: me.availability || [],
        timezone: me.timezone || "America/Sao_Paulo",
        serves_fp: !!me.serves_fp,
        city: me.city || "",
        state: me.state || "",
      });
      toast.success("Perfil salvo.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const upload = async (e, kind) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Persiste o rascunho ANTES do upload — sem isso o load() posterior perde
    // tudo que o usuário digitou desde o último Salvar rascunho.
    await persistDraft();
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/professional/me/upload?kind=${kind}`, fd);
      toast.success("Arquivo enviado.");
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Erro no upload"); }
    e.target.value = "";
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      await save();
      await api.post("/professional/me/submit");
      toast.success("Enviado! O admin RAF vai revisar em até 48h.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSubmitting(false); }
  };

  const addSlot = () => {
    set("availability", [...(me.availability || []),
      { weekday: 1, start: "09:00", end: "12:00" }]);
  };
  const removeSlot = (i) => set("availability", me.availability.filter((_, idx) => idx !== i));
  const updateSlot = (i, patch) => set("availability",
    me.availability.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const status = STATUS_MAP[me.status || "draft"];

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="professional-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Perfil profissional</div>
          <h1 className="font-display font-bold text-[#0B2B62] text-3xl mt-1">Olá, {me.name}</h1>
        </div>
        <span data-testid="prof-status" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-widest ${status.cls}`}>
          <status.Icon className="w-3.5 h-3.5" /> {status.label}
        </span>
      </div>

      {me.status === "rejected" && me.rejection_reason && (
        <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-sm text-red-900 flex gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div><b>Motivo:</b> {me.rejection_reason}<br /><span className="text-red-700">Ajuste o perfil e envie novamente.</span></div>
        </div>
      )}

      {/* Planos de assinatura Ouro/Prata/Bronze — Iter 68.7 */}
      {me.status === "approved" && <SubscriptionPlansCard kind="prof" entity={me} />}

      {/* Iter 68.51 — Minhas comissões (só profissional aprovado) */}
      {me.status === "approved" && <ProfessionalFinancePanel />}

      {/* Iter 68.119.n — Meus convênios (só profissional aprovado) */}
      {me.status === "approved" && <ProfessionalConveniosSection />}
      {me.status === "approved" && <ProfessionalInsuranceFinancePanel />}

      <Section title="Dados básicos" icon={UserCircle2}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Nome de exibição (aparecerá na vitrine)" required>
            <input value={me.display_name || ""} onChange={(e) => set("display_name", e.target.value)}
              data-testid="prof-display-name"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <Field label="Telefone (WhatsApp preferencial)">
            <input value={me.phone || ""} onChange={(e) => set("phone", e.target.value)}
              placeholder="(11) 99999-9999" data-testid="prof-phone"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <Field label="Cidade (atendimento presencial/domiciliar)">
            <input value={me.city || ""} onChange={(e) => set("city", e.target.value)}
              placeholder="São Paulo" data-testid="prof-city"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <Field label="Estado (UF)">
            <input maxLength={2} value={me.state || ""} onChange={(e) => set("state", e.target.value.toUpperCase())}
              placeholder="SP" data-testid="prof-state"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Categorias * (marque todas que atender)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {categories.map((c) => {
                const active = (me.categories || []).includes(c);
                return (
                  <button key={c} type="button" onClick={() => toggleCat(c)}
                    data-testid={`prof-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      active ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B2B62]"
                    }`}>{c}</button>
                );
              })}
            </div>
          </div>
          <div className="md:col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!me.serves_fp}
                onChange={(e) => set("serves_fp", e.target.checked)}
                data-testid="prof-serves-fp"
                className="mt-0.5" />
              <span className="text-sm text-amber-900">
                <b>Atendo pessoas idosas</b> — meu perfil aparece também na vitrine
                <a href="/filho-presente/profissionais" target="_blank" rel="noreferrer" className="underline mx-1">Filho Presente</a>
                (cuidado geriátrico coordenado com a família). Categorias geriátricas já ativam isso automaticamente.
              </span>
            </label>
          </div>
        </div>
      </Section>

      <Section title="Registro no conselho" icon={ShieldCheck}>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Conselho *">
            <select value={me.council_type || ""} onChange={(e) => set("council_type", e.target.value)}
              data-testid="prof-council-type"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="">Selecione</option>
              {COUNCILS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Número *">
            <input value={me.council_number || ""} onChange={(e) => set("council_number", e.target.value)}
              data-testid="prof-council-number"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
          <Field label="UF *">
            <input maxLength={2} value={me.council_state || ""} onChange={(e) => set("council_state", e.target.value.toUpperCase())}
              placeholder="SP" data-testid="prof-council-state"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
        </div>
      </Section>

      <Section title="Sobre você" icon={FileText}>
        <Field label="Bio pública (será exibida à família) *">
          <textarea rows={5} value={me.bio || ""} onChange={(e) => set("bio", e.target.value)}
            data-testid="prof-bio"
            placeholder="Ex.: Psiquiatra com 10 anos de experiência em dependência química, formação em Harvard Medical School…"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </Field>
        <Field label="Currículo resumido (texto livre — opcional)">
          <textarea rows={4} value={me.curriculum_text || ""} onChange={(e) => set("curriculum_text", e.target.value)}
            data-testid="prof-curriculum-text"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </Field>
      </Section>

      <Section title="Foto & documentos" icon={UserCircle2}>
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex flex-col items-center gap-2">
            {me.photo_url ? (
              <img src={me.photo_url} alt="" className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-md" data-testid="prof-photo-preview" />
            ) : (
              <div className="w-32 h-32 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><UserCircle2 className="w-12 h-12" /></div>
            )}
            <button type="button" onClick={() => photoRef.current?.click()} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="prof-photo-btn">
              {me.photo_url ? "Trocar foto" : "Enviar foto"}
            </button>
            <input ref={photoRef} hidden type="file" accept="image/*" onChange={(e) => upload(e, "photo")} />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Currículo em PDF (opcional)</div>
            {me.curriculum_file_url ? (
              <a href={me.curriculum_file_url} target="_blank" rel="noreferrer" className="text-sm text-[#0B2B62] underline">Ver PDF anexado</a>
            ) : <span className="text-xs text-slate-400">Nenhum arquivo</span>}
            <div className="mt-2">
              <button type="button" onClick={() => curriculumRef.current?.click()} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="prof-cv-btn">
                {me.curriculum_file_url ? "Trocar currículo" : "Enviar currículo"}
              </button>
              <input ref={curriculumRef} hidden type="file" accept=".pdf,image/*" onChange={(e) => upload(e, "curriculum")} />
            </div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mt-6 mb-2">
              Documentos de credenciamento * ({(me.documents || []).length})
            </div>
            <p className="text-xs text-slate-500 mb-2">Ex.: RG/CNH, carteirinha do conselho, diploma, comprovante de especialidade.</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {(me.documents || []).map((url) => (
                <div key={url} className="flex items-center gap-1.5 border border-slate-200 rounded-md px-2 py-1 text-xs bg-white">
                  <a href={url} target="_blank" rel="noreferrer" className="text-[#0B2B62] underline max-w-[160px] truncate">{url.split("/").pop()}</a>
                  <button onClick={async () => { await persistDraft(); await api.delete(`/professional/me/document?url=${encodeURIComponent(url)}`); load(); }} className="text-red-500 hover:text-red-700" data-testid="prof-doc-remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => docRef.current?.click()} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="prof-doc-btn">
              Adicionar documento
            </button>
            <input ref={docRef} hidden type="file" accept=".pdf,image/*" onChange={(e) => upload(e, "document")} />
          </div>
        </div>
      </Section>

      <Section title="Consulta & valores" icon={DollarSign}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Duração padrão da consulta *">
            <select value={me.session_duration || 50} onChange={(e) => set("session_duration", parseInt(e.target.value, 10))}
              data-testid="prof-duration"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value={30}>30 minutos</option>
              <option value={50}>50 minutos (padrão psicologia)</option>
              <option value={60}>60 minutos</option>
            </select>
          </Field>
          <Field label="Valor por consulta (R$) *" hint={
            me.session_value ? `Você recebe R$ ${(me.session_value * 0.65).toFixed(2)} · RAF fica com R$ ${(me.session_value * 0.35).toFixed(2)} (35%)` : "35% de comissão RAF será descontada"
          }>
            <input type="number" min="0" step="0.01" value={me.session_value || ""} onChange={(e) => set("session_value", e.target.value)}
              data-testid="prof-value"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </Field>
        </div>
      </Section>

      <Section title="Agenda semanal" icon={Clock}>
        <p className="text-sm text-slate-600 mb-3">Configure janelas recorrentes. A RAF só direciona famílias em horários que você deixou disponíveis.</p>
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Fuso horário</label>
          <select value={me.timezone || "America/Sao_Paulo"}
            onChange={(e) => set("timezone", e.target.value)}
            data-testid="prof-timezone"
            className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
            <option value="America/Sao_Paulo">Brasília (BRT · São Paulo)</option>
            <option value="America/Fortaleza">Fortaleza (BRT · sem DST)</option>
            <option value="America/Manaus">Manaus (AMT · UTC-4)</option>
            <option value="America/Cuiaba">Cuiabá (AMT · UTC-4)</option>
            <option value="America/Rio_Branco">Rio Branco (ACT · UTC-5)</option>
            <option value="America/Noronha">Fernando de Noronha (UTC-2)</option>
            <option value="Europe/Lisbon">Lisboa (WET · expat PT)</option>
            <option value="Europe/London">Londres (GMT)</option>
            <option value="America/New_York">Nova York (EST)</option>
            <option value="America/Los_Angeles">Los Angeles (PST)</option>
            <option value="UTC">UTC</option>
          </select>
          <span className="text-[11px] text-slate-500">Os horários das janelas abaixo são interpretados nesse fuso.</span>
        </div>
        <div className="space-y-2">
          {(me.availability || []).map((s, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`prof-slot-${i}`}>
              <select value={s.weekday} onChange={(e) => updateSlot(i, { weekday: parseInt(e.target.value, 10) })}
                className="px-2 py-1.5 border border-slate-300 rounded-md text-sm">
                {WEEKDAYS.map((d, wi) => <option key={wi} value={wi}>{d}</option>)}
              </select>
              <input type="time" value={s.start} onChange={(e) => updateSlot(i, { start: e.target.value })}
                className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
              <span className="text-slate-400">até</span>
              <input type="time" value={s.end} onChange={(e) => updateSlot(i, { end: e.target.value })}
                className="px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
              <button onClick={() => removeSlot(i)} className="text-red-500 hover:text-red-700 p-1.5" data-testid={`prof-slot-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={addSlot} className="mt-2 text-xs px-3 py-1.5 border border-dashed border-slate-400 rounded-md text-slate-600 hover:border-emerald-600 hover:text-emerald-700" data-testid="prof-slot-add">
            + Adicionar janela
          </button>
        </div>
      </Section>

      <BookingsPanel />

      {/* Ações */}
      <div className="sticky bottom-4 z-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 shadow-md">
        <div className="text-sm text-slate-600 flex-1">
          <b>Ações finais:</b> salve seu perfil e envie para análise — o admin RAF aprova em até 48h.
        </div>
        <button onClick={save} disabled={saving}
          data-testid="prof-save"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm font-semibold text-slate-700 disabled:opacity-50">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Salvar rascunho
        </button>
        <button onClick={submitForReview} disabled={submitting || me.status === "approved"}
          data-testid="prof-submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold disabled:opacity-50">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          <Send className="w-4 h-4" /> Enviar para aprovação
        </button>
      </div>
    </div>
  );
};

/**
 * Painel de consultas do profissional. Mostra apenas metadados —
 * NUNCA e-mail/telefone/nome completo da família (regra de PII do termo).
 * Permite recusar reservas ainda não concluídas.
 */
const BookingsPanel = () => {
  const [items, setItems] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  const load = () => api.get("/bookings/professional").then(r => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const decline = async (id) => {
    if (!window.confirm("Recusar este caso? A família será notificada e a RAF fará o reembolso ou redirecionará para outro profissional.")) return;
    setDecliningId(id);
    try {
      await api.post(`/bookings/${id}/decline`);
      toast.success("Caso recusado. RAF será notificada.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao recusar"); }
    finally { setDecliningId(null); }
  };

  const markComplete = async (id) => {
    if (!window.confirm("Confirmar que a consulta foi realizada? Isso libera sua comissão (65%) no próximo repasse.")) return;
    setMarkingId(id);
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success("Consulta marcada como realizada.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setMarkingId(null); }
  };

  const markNoShow = async (id) => {
    if (!window.confirm("Marcar como 'não compareceu' (família faltou)? Você ainda recebe integral — o horário foi reservado.")) return;
    setMarkingId(id);
    try {
      await api.post(`/bookings/${id}/no-show`);
      toast.success("Marcado como não compareceu. Sua comissão está preservada.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setMarkingId(null); }
  };

  return (
    <Section title="Minhas consultas" icon={Calendar}>
      {items === null && <div className="text-sm text-slate-500">Carregando…</div>}
      {items && items.length === 0 && (
        <div className="text-sm text-slate-500">Nenhuma consulta paga ainda. Assim que uma família reservar, aparecerá aqui — sem expor dados pessoais.</div>
      )}
      {items && items.length > 0 && (
        <div className="space-y-3" data-testid="prof-bookings-list">
          {items.map(b => {
            const when = new Date(b.scheduled_at);
            const isFuture = when > new Date();
            const isPast = !isFuture;
            const canDecline = b.status === "paid" && isFuture;
            const canMark = b.status === "paid" && isPast;
            const statusLabel = b.status === "paid" ? "Pago" :
                                b.status === "completed" ? "Concluída" :
                                b.status === "no_show" ? "Não compareceu" :
                                "Recusada";
            const statusClasses = b.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                                  b.status === "completed" ? "bg-slate-100 text-slate-700 border border-slate-200" :
                                  b.status === "no_show" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                                  "bg-red-50 text-red-700 border border-red-200";
            return (
              <div key={b.id} className="border border-slate-200 rounded-lg p-4 flex flex-wrap items-start gap-4" data-testid={`booking-${b.id}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    {when.toLocaleString("pt-BR", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="mt-1 font-display font-bold text-[#0B2B62] text-lg">Paciente: {b.family_display}</div>
                  {b.notes && <p className="mt-1 text-sm text-slate-600 text-left"><b>Motivo:</b> {b.notes}</p>}
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                    <span>Duração: {b.duration_min} min</span>
                    <span>Valor total: R$ {(b.session_value || 0).toFixed(2)}</span>
                    <span className="text-emerald-700 font-bold">Você recebe: R$ {(b.professional_share || 0).toFixed(2)}</span>
                    <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${statusClasses}`}
                          data-testid={`booking-status-${b.id}`}>
                      {statusLabel}
                    </span>
                  </div>
                  {b.completed_at && (
                    <p className="mt-1 text-[11px] text-slate-500">
                      Marcada como realizada em {new Date(b.completed_at).toLocaleString("pt-BR")}
                      {b.completed_by === "auto_cron" && <span className="text-slate-400"> (automaticamente 1h após o horário)</span>}
                    </p>
                  )}
                  {b.no_show_at && (
                    <p className="mt-1 text-[11px] text-amber-700">
                      Não compareceu — registrado em {new Date(b.no_show_at).toLocaleString("pt-BR")}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 min-w-[180px]">
                  {b.status === "paid" && (
                    <Link to={`/consulta/${b.id}`} data-testid={`booking-join-${b.id}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold">
                      <Video className="w-3.5 h-3.5" /> Entrar na sala
                    </Link>
                  )}
                  {canMark && (
                    <>
                      <button onClick={() => markComplete(b.id)} disabled={markingId === b.id}
                        data-testid={`booking-complete-${b.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0B2B62] hover:bg-[#0a2455] text-white text-xs font-bold disabled:opacity-50">
                        {markingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Consulta realizada
                      </button>
                      <button onClick={() => markNoShow(b.id)} disabled={markingId === b.id}
                        data-testid={`booking-noshow-${b.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-400 text-amber-700 hover:bg-amber-50 text-xs font-semibold disabled:opacity-50">
                        <XCircle className="w-3.5 h-3.5" />
                        Não compareceu
                      </button>
                    </>
                  )}
                  {canDecline && (
                    <button onClick={() => decline(b.id)} disabled={decliningId === b.id}
                      data-testid={`booking-decline-${b.id}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-xs font-semibold disabled:opacity-50">
                      {decliningId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                      Recusar este caso
                    </button>
                  )}
                </div>
                {/* Prontuário SOAP + Documentos + Triagem IA — LGPD/CFM/CFP */}
                <div className="w-full">
                  <PostConsultationPanel bookingId={b.id} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-[#0B2B62]" strokeWidth={2} />
      <h2 className="font-display font-bold text-lg text-[#0B2B62]">{title}</h2>
    </div>
    {children}
  </section>
);

/**
 * Gate de aceite dos termos comerciais RAF.
 *
 * Aparece somente para profissionais que ainda não têm `terms_accepted_at`.
 * Mostra a íntegra dos termos (taxa mensal, comissão 35%, exclusividade
 * RAF, direito de recusa, cláusula de banimento + medidas legais) e bloqueia
 * o resto do dashboard até o aceite.
 */
const TermsAcceptanceGate = ({ professionalName, onAccepted }) => {
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const accept = async () => {
    if (!accepted) { toast.error("Marque a caixa de aceite antes de continuar."); return; }
    setSaving(true);
    try {
      await api.post("/professional/me/accept-terms");
      toast.success("Termos aceitos. Bem-vindo(a) à RAF Direct!");
      onAccepted && onAccepted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar aceite");
    } finally { setSaving(false); }
  };
  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="professional-terms-gate">
      <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1.5">
        <Scale className="w-3.5 h-3.5" /> Termos comerciais RAF Direct
      </div>
      <h1 className="font-display font-bold text-[#0B2B62] text-3xl mt-1">Olá, {professionalName}. Leia e aceite antes de continuar.</h1>
      <p className="text-slate-600 mt-2 max-w-2xl leading-relaxed text-left">
        Estes termos regem sua relação como profissional credenciado. Leia com atenção — sem o aceite formal, seu cadastro permanece incompleto e nenhuma indicação é enviada à sua agenda.
      </p>

      <div className="mt-6 space-y-4">
        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-700" /> Taxa mensal de credenciamento</div>
          <p className="text-sm text-slate-700 mt-2">
            Após a aprovação do meu cadastro pelo admin RAF Direct, autorizo o débito recorrente mensal de <b>R$ 100,00</b>, cobrado via Stripe, para manter meu perfil ativo na vitrine e receber indicações.
          </p>
        </div>
        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-700" /> Comissão RAF por consulta</div>
          <p className="text-sm text-slate-700 mt-2">
            Reconheço que <b>35% do valor de cada consulta indicada</b> pela RAF fica com a plataforma como remuneração pelos serviços de triagem inteligente, encaminhamento clínico e infraestrutura tecnológica. A família paga diretamente à RAF; o repasse dos 65% ao profissional acontece <b>mensalmente via PIX</b>, consolidando todas as consultas realizadas no período.
          </p>
        </div>
        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2"><Clock className="w-5 h-5 text-emerald-700" /> Atendimento exclusivo pelo sistema RAF</div>
          <p className="text-sm text-slate-700 mt-2">
            Comprometo-me a atender <b>exclusivamente dentro do sistema RAF Direct</b>, utilizando a sala de vídeo integrada e o prontuário digital fornecidos pela plataforma. Reconheço que os dados de contato pessoal da família (telefone, e-mail, endereço) NÃO são compartilhados comigo justamente para preservar a intermediação segura.
          </p>
        </div>
        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2"><XCircle className="w-5 h-5 text-amber-600" /> Direito de recusa do caso</div>
          <p className="text-sm text-slate-700 mt-2">
            Tenho o direito de <b>recusar atender um perfil</b> encaminhado pela triagem — por sobrecarga de agenda, incompatibilidade de escopo clínico ou qualquer motivo técnico —, informando a recusa dentro do próprio sistema. A RAF então redireciona o caso para outro profissional credenciado, <b>sem que dados pessoais do paciente sejam expostos</b>.
          </p>
        </div>
        <div className="p-5 rounded-lg border border-red-300 bg-red-50">
          <div className="font-display font-bold text-red-800 flex items-center gap-2"><Ban className="w-5 h-5" /> Banimento por atendimento externo</div>
          <p className="text-sm text-red-900 mt-2 leading-relaxed">
            <b>Cláusula grave:</b> se a RAF Direct identificar que o profissional retirou um paciente ou família da plataforma para atendimento externo — por qualquer canal (WhatsApp pessoal, e-mail, consultório presencial, sistema de terceiros) —, será <b>automaticamente banido</b> do sistema, sem direito a novo credenciamento, e o caso poderá acarretar as <b>medidas legais cabíveis</b>, incluindo cobrança das comissões devidas, indenização por quebra contratual e comunicação ao conselho de classe.
          </p>
        </div>
        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-700" /> Documentos de credenciamento</div>
          <p className="text-sm text-slate-700 mt-2">
            Comprometo-me a enviar cópia legível do registro no conselho profissional (CRM, CRP, CFN, COREN, CREF, etc.), comprovante de especialidade quando aplicável e demais documentos solicitados. Cadastros incompletos ou com dados inconsistentes serão reprovados.
          </p>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-lg border border-amber-300 bg-amber-50">
        <label className="inline-flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
            data-testid="prof-terms-checkbox" className="mt-1" />
          <span className="text-sm text-amber-900 font-semibold leading-relaxed">
            Li integralmente e <b>aceito</b> todos os termos comerciais acima. Estou ciente de que o aceite fica registrado com data/hora para fins contratuais e que o descumprimento pode acarretar as medidas descritas.
          </span>
        </label>
      </div>

      <button onClick={accept} disabled={!accepted || saving}
        data-testid="prof-terms-accept-btn"
        className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Aceitar termos e continuar cadastro
      </button>
    </div>
  );
};

const Field = ({ label, hint, required, children }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default ProfessionalDashboard;

/**
 * Iter 68.51 — Painel "Minhas comissões" do profissional. Mostra o que o
 * profissional vai receber por consultas pagas/completas/no-show (share 65%
 * cheio pra paid/completed, 50% em no-show). Total do mês + histórico 12m.
 */
const brlFin = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const ProfessionalFinancePanel = () => {
  const [d, setD] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    api.get("/professional/me/finance").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  return (
    <Section title="Minhas comissões (65% de cada consulta)" icon={Calendar}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="prof-finance-cards">
        <MiniCard label={`Mês (${d.current_month})`} value={brlFin(d.month_share)} accent="emerald" testId="prof-fin-month" />
        <MiniCard label="Total no período" value={brlFin(d.total_share)} accent="slate" testId="prof-fin-total" />
        <MiniCard label="Consultas contadas" value={String(d.count)} accent="slate" testId="prof-fin-count" />
        <MiniCard label="Split" value="65% você · 35% RAF" accent="amber" testId="prof-fin-split" />
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        Inclui consultas pagas, concluídas e no-show da família (que paga 50%). Repasses são feitos pela RAF (verifique com o admin a frequência).
      </p>
      {d.items.length > 0 && (
        <>
          <button onClick={() => setExpanded((v) => !v)} data-testid="prof-finance-toggle"
            className="mt-3 text-sm text-[#0B2B62] font-semibold hover:underline">
            {expanded ? "Fechar histórico" : `Ver histórico (${d.items.length})`}
          </button>
          {expanded && (
            <div className="mt-2 border border-slate-200 rounded-md overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <div className="col-span-3">Data</div>
                <div className="col-span-3">Paciente</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Valor bruto</div>
                <div className="col-span-2 text-right">Sua parte</div>
              </div>
              {d.items.map((it) => (
                <div key={it.booking_id} className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 text-xs items-center"
                     data-testid={`prof-fin-row-${it.booking_id}`}>
                  <div className="col-span-3 text-slate-600">
                    {new Date(it.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="col-span-3 text-slate-700">{it.family_display}</div>
                  <div className="col-span-2 text-[10px] uppercase tracking-widest text-slate-500">{it.status}</div>
                  <div className="col-span-2 text-right tabular-nums text-slate-700">{brlFin(it.session_value)}</div>
                  <div className="col-span-2 text-right tabular-nums font-bold text-emerald-700">{brlFin(it.professional_share)}</div>
                </div>
              ))}
              {d.items.map((it) => (
                <div key={`m-${it.booking_id}`} className="md:hidden p-3 border-b border-slate-100 text-xs">
                  <div className="flex justify-between gap-2 items-baseline">
                    <div className="font-semibold text-[#0B2B62]">{it.family_display}</div>
                    <div className="font-bold text-emerald-700 tabular-nums">{brlFin(it.professional_share)}</div>
                  </div>
                  <div className="text-slate-500 mt-0.5">
                    {new Date(it.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · {it.status} · bruto {brlFin(it.session_value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
};

const MiniCard = ({ label, value, accent = "slate", testId }) => {
  const CLS = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    slate:   "bg-white border-slate-200 text-[#0B2B62]",
  };
  return (
    <div className={`p-3 border rounded-lg ${CLS[accent]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="font-display font-bold text-xl mt-1 tabular-nums">{value}</div>
    </div>
  );
};
